import React, { useEffect, useState } from "react";
import { fetchContent } from "../../services/api";
import { updateContent, extractErrorMessage } from "../../services/adminApi";
import { DEFAULT_CONTENT } from "../../data/contentDefaults";
import { TextField, TextAreaField, SaveBar } from "../FormControls";

const TAB_OPTIONS = [
    { value: "", label: "— без ссылки —" },
    { value: "about", label: "О проекте" },
    { value: "map", label: "Карта" },
    { value: "methodology", label: "Методика" },
    { value: "data", label: "Данные" },
];

const MethodologyEditor = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        fetchContent()
            .then((content) =>
                setData(content?.methodology || DEFAULT_CONTENT.methodology)
            )
            .catch(() => setData(DEFAULT_CONTENT.methodology))
            .finally(() => setLoading(false));
    }, []);

    const patch = (fields) => {
        setData((prev) => ({ ...prev, ...fields }));
        setSavedAt(null);
    };

    const patchStep = (index, fields) => {
        setData((prev) => {
            const steps = prev.steps.slice();
            steps[index] = { ...steps[index], ...fields };
            return { ...prev, steps };
        });
        setSavedAt(null);
    };

    const addStep = () =>
        setData((prev) => ({
            ...prev,
            steps: [...prev.steps, { title: "", description: "" }],
        }));

    const removeStep = (index) =>
        setData((prev) => ({
            ...prev,
            steps: prev.steps.filter((_, i) => i !== index),
        }));

    const patchResultValue = (index, value) => {
        setData((prev) => {
            const resultValues = prev.resultValues.slice();
            resultValues[index] = value;
            return { ...prev, resultValues };
        });
        setSavedAt(null);
    };

    const addResultValue = () =>
        setData((prev) => ({
            ...prev,
            resultValues: [...prev.resultValues, ""],
        }));

    const removeResultValue = (index) =>
        setData((prev) => ({
            ...prev,
            resultValues: prev.resultValues.filter((_, i) => i !== index),
        }));

    const patchQualityCard = (index, fields) => {
        setData((prev) => {
            const qualityCards = prev.qualityCards.slice();
            qualityCards[index] = { ...qualityCards[index], ...fields };
            return { ...prev, qualityCards };
        });
        setSavedAt(null);
    };

    const addQualityCard = () =>
        setData((prev) => ({
            ...prev,
            qualityCards: [
                ...prev.qualityCards,
                { title: "", description: "", linkLabel: "", targetTab: null },
            ],
        }));

    const removeQualityCard = (index) =>
        setData((prev) => ({
            ...prev,
            qualityCards: prev.qualityCards.filter((_, i) => i !== index),
        }));

    const handleSave = async () => {
        setSaving(true);
        setError("");
        try {
            const saved = await updateContent("methodology", data);
            setData(saved);
            setSavedAt(Date.now());
        } catch (err) {
            setError(extractErrorMessage(err, "Не удалось сохранить"));
        } finally {
            setSaving(false);
        }
    };

    if (loading || !data) return <p>Загрузка…</p>;

    return (
        <div className="admin-section">
            <h2 className="admin-section-title">Методика</h2>

            <TextField
                label="Заголовок страницы"
                value={data.heading}
                onChange={(v) => patch({ heading: v })}
            />
            <TextAreaField
                label="Подзаголовок"
                value={data.subheading}
                onChange={(v) => patch({ subheading: v })}
                rows={2}
            />
            <TextField
                label="Заголовок блока этапов"
                value={data.stepsTitle}
                onChange={(v) => patch({ stepsTitle: v })}
            />

            <h3 className="admin-subsection-title">Этапы расчёта</h3>
            {data.steps.map((step, i) => (
                <div className="admin-repeatable-item" key={i}>
                    <div className="admin-repeatable-header">
                        <span>Этап {i + 1}</span>
                        <button
                            type="button"
                            className="admin-remove-button"
                            onClick={() => removeStep(i)}
                        >
                            Удалить
                        </button>
                    </div>
                    <TextField
                        label="Заголовок"
                        value={step.title}
                        onChange={(v) => patchStep(i, { title: v })}
                    />
                    <TextAreaField
                        label="Описание"
                        value={step.description}
                        onChange={(v) => patchStep(i, { description: v })}
                        rows={2}
                    />
                </div>
            ))}
            <button type="button" className="admin-add-button" onClick={addStep}>
                + Добавить этап
            </button>

            <TextField
                label="Заголовок карточки формулы"
                value={data.formulaTitle}
                onChange={(v) => patch({ formulaTitle: v })}
            />
            <p className="admin-hint">
                Сами формулы (картинки Epot/Cpot) в этом редакторе не
                меняются — обновите файлы formula-epot.png / formula-cpot.png
                на сервере.
            </p>
            <TextField
                label="Подпись «Результат»"
                value={data.resultLabel}
                onChange={(v) => patch({ resultLabel: v })}
            />

            <h3 className="admin-subsection-title">Строки результата</h3>
            {data.resultValues.map((value, i) => (
                <div className="admin-repeatable-item" key={i}>
                    <div className="admin-repeatable-header">
                        <span>Строка {i + 1}</span>
                        <button
                            type="button"
                            className="admin-remove-button"
                            onClick={() => removeResultValue(i)}
                        >
                            Удалить
                        </button>
                    </div>
                    <TextField
                        label="Текст"
                        value={value}
                        onChange={(v) => patchResultValue(i, v)}
                    />
                </div>
            ))}
            <button
                type="button"
                className="admin-add-button"
                onClick={addResultValue}
            >
                + Добавить строку
            </button>

            <h3 className="admin-subsection-title">Литературный источник</h3>
            <TextAreaField
                label="Текст цитаты"
                value={data.citation}
                onChange={(v) => patch({ citation: v })}
                rows={4}
            />
            <TextField
                label="Ссылка (DOI)"
                value={data.citationUrl}
                onChange={(v) => patch({ citationUrl: v })}
            />

            <TextField
                label="Заголовок блока «Документация и контроль качества»"
                value={data.qualityTitle}
                onChange={(v) => patch({ qualityTitle: v })}
            />

            <h3 className="admin-subsection-title">
                Карточки документации
            </h3>
            {data.qualityCards.map((card, i) => (
                <div className="admin-repeatable-item" key={i}>
                    <div className="admin-repeatable-header">
                        <span>Карточка {i + 1}</span>
                        <button
                            type="button"
                            className="admin-remove-button"
                            onClick={() => removeQualityCard(i)}
                        >
                            Удалить
                        </button>
                    </div>
                    <TextField
                        label="Заголовок"
                        value={card.title}
                        onChange={(v) => patchQualityCard(i, { title: v })}
                    />
                    <TextAreaField
                        label="Описание"
                        value={card.description}
                        onChange={(v) =>
                            patchQualityCard(i, { description: v })
                        }
                        rows={2}
                    />
                    <TextField
                        label="Текст ссылки"
                        value={card.linkLabel}
                        onChange={(v) =>
                            patchQualityCard(i, { linkLabel: v })
                        }
                    />
                    <label className="admin-field">
                        <span>Куда ведёт ссылка</span>
                        <select
                            value={card.targetTab || ""}
                            onChange={(e) =>
                                patchQualityCard(i, {
                                    targetTab: e.target.value || null,
                                })
                            }
                        >
                            {TAB_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            ))}
            <button
                type="button"
                className="admin-add-button"
                onClick={addQualityCard}
            >
                + Добавить карточку
            </button>

            <SaveBar
                onSave={handleSave}
                saving={saving}
                savedAt={savedAt}
                error={error}
            />
        </div>
    );
};

export default MethodologyEditor;
